import type {
  AdminCertification,
  AdminCertificationLinkedStylesResponse,
  AdminCertificationsResponse,
  CreateAdminCertificationPayload,
} from '@/api/(admin)/certifications/types';
import type { RawCertificationComment } from '@/api/certifications/comments';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * The unscoped certificate roster: every company's claims, archived ones
 * included; removed ones only when the filter asks for them.
 */
export async function fetchAdminCertifications(
  qs: string,
): Promise<AdminCertificationsResponse> {
  return apiFetch<AdminCertificationsResponse>(
    `/api/admin/claims?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAdminCertification(
  id: string,
): Promise<{ data: AdminCertification }> {
  return apiFetch<{ data: AdminCertification }>(
    `/api/admin/claims/${id}`,
    {},
    API_ROOT_URL,
  );
}

export async function createAdminCertification(
  payload: CreateAdminCertificationPayload,
): Promise<{ data: AdminCertification }> {
  return apiFetch<{ data: AdminCertification }>(
    '/api/admin/claims',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

// The four bulk state changes, each taking the same `{ ids }` body. Note the
// verbs differ: three are `PATCH` and delete is `PUT`, which is what the
// endpoints accept rather than a choice made here.

/** `PATCH /api/admin/claims/bulk-archive`, answering the ids it archived. */
export async function bulkArchiveAdminCertifications(
  ids: string[],
): Promise<{ data: { archivedClaimIds: string[] } }> {
  return apiFetch<{ data: { archivedClaimIds: string[] } }>(
    '/api/admin/claims/bulk-archive',
    { method: 'PATCH', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

/** `PATCH /api/admin/claims/bulk-unarchive`, answering the ids it unarchived. */
export async function bulkUnarchiveAdminCertifications(
  ids: string[],
): Promise<{ data: { unarchivedClaimIds: string[] } }> {
  return apiFetch<{ data: { unarchivedClaimIds: string[] } }>(
    '/api/admin/claims/bulk-unarchive',
    { method: 'PATCH', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

/** `PUT /api/admin/claims/bulk-delete`, answering the ids it removed. */
export async function bulkRemoveAdminCertifications(
  ids: string[],
): Promise<{ data: { removedClaimIds: string[] } }> {
  return apiFetch<{ data: { removedClaimIds: string[] } }>(
    '/api/admin/claims/bulk-delete',
    { method: 'PUT', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

/** `PATCH /api/admin/claims/bulk-restore`, answering the ids it restored. */
export async function bulkRestoreAdminCertifications(
  ids: string[],
): Promise<{ data: { restoredClaimIds: string[] } }> {
  return apiFetch<{ data: { restoredClaimIds: string[] } }>(
    '/api/admin/claims/bulk-restore',
    { method: 'PATCH', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

/** One review verdict. */
export type AdminCertificationVerdict = 'approve' | 'reject' | 'not-verifiable';

/**
 * `PUT /api/admin/claims/:id/approve`. Answers the approved certificate in the
 * platform envelope.
 */
export async function approveAdminCertification(
  id: string,
): Promise<{ data: AdminCertification }> {
  return apiFetch<{ data: AdminCertification }>(
    `/api/admin/claims/${id}/approve`,
    { method: 'PUT' },
    API_ROOT_URL,
  );
}

/**
 * `PUT /api/admin/claims/:id/reject`: back to `DRAFT` for the company to
 * correct. Answers the rejected certificate in the platform envelope.
 */
export async function rejectAdminCertification(
  id: string,
): Promise<{ data: AdminCertification }> {
  return apiFetch<{ data: AdminCertification }>(
    `/api/admin/claims/${id}/reject`,
    { method: 'PUT' },
    API_ROOT_URL,
  );
}

/**
 * `PUT /api/admin/claims/:id/not-verifiable`: archives what an approval would.
 * Answers the certificate in the platform envelope.
 */
export async function markAdminCertificationNotVerifiable(
  id: string,
): Promise<{ data: AdminCertification }> {
  return apiFetch<{ data: AdminCertification }>(
    `/api/admin/claims/${id}/not-verifiable`,
    { method: 'PUT' },
    API_ROOT_URL,
  );
}

/**
 * Saves an edited certificate.
 *
 * `PUT /api/admin/claims/:id` replaces the record, so a partial patch drops
 * whatever it leaves out — callers send the merged row, as the customer detail
 * page does through `buildClaimUpdatePayload`. Answers the saved certificate
 * in the platform envelope.
 */
export async function updateAdminCertification(
  id: string,
  payload: Record<string, unknown>,
): Promise<{ data: AdminCertification }> {
  return apiFetch<{ data: AdminCertification }>(
    `/api/admin/claims/${id}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

/** The same row the customer endpoints return — one thread, two scopes. */
export type AdminCertificationComment = RawCertificationComment;

/** `GET /api/admin/claims/list-comments`: the whole thread, internal notes included. */
export async function fetchAdminCertificationComments(
  claimId: string,
): Promise<{ data: AdminCertificationComment[] }> {
  return apiFetch<{ data: AdminCertificationComment[] }>(
    `/api/admin/claims/list-comments?claimId=${encodeURIComponent(claimId)}`,
    {},
    API_ROOT_URL,
  );
}

export async function addAdminCertificationComment(body: {
  claimId: string;
  companyId: string;
  content: string;
}): Promise<{ data: AdminCertificationComment }> {
  return apiFetch<{ data: AdminCertificationComment }>(
    '/api/admin/claims/add-comment',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/** The products a certificate is linked to, unscoped — the management page's
 *  counterpart of `fetchCertificationLinkedStyles`. */
export async function fetchAdminCertificationLinkedStyles(
  id: string,
  qs: string,
): Promise<AdminCertificationLinkedStylesResponse> {
  return apiFetch<AdminCertificationLinkedStylesResponse>(
    `/api/admin/claims/${id}/list-styles?${qs}`,
    {},
    API_ROOT_URL,
  );
}
